import { P2GBox } from '@parcel2go/peacock-legacy-core/dist/box';
import { P2GText } from '@parcel2go/peacock-legacy-core/dist/typography';
import { P2GAlert } from '@parcel2go/peacock-legacy-core/dist/alert';
import { P2GFade } from '@parcel2go/peacock-legacy-core/dist/transitions';
import React, { useContext, useEffect, useState } from 'react';
import {
  ActionType,
  GetInstructionsApiResponseModel,
  Integration,
  IntegrationType,
  StrVal,
} from '../services/api/types';
import { Settings } from '../settings';
import { P2GLink } from '@parcel2go/peacock-legacy-core/dist/link';
import { P2GGrid } from '@parcel2go/peacock-legacy-core/dist/grid';
import { P2GDivider } from '@parcel2go/peacock-legacy-core/dist//divider';
import Steps from './Steps';
import { getActionInstructions, postActionValues } from '../services/api';
import { P2GProgress } from '@parcel2go/peacock-legacy-core/dist/progress';
import { SumNumber } from '../services/helpers/Array';
import InstructionsFooter from './InstructionsFooter';
import { ValidationErrorMessages } from '../services/helpers/ValidateIntegration';
import { NotificationContext } from '../services/contexts/NotificationContext';
import { AccountActionResponseHelper } from '../services/helpers/AccountLinkingResponseHelper';

interface props {
  integrationSlug: string;
  integrationType: IntegrationType;
  onComplete: (accountId: string, followUpAction?: ActionType) => void;
  action: ActionType;
  accountId: string;
  configRequired: boolean;
  onError?: () => void;
  onInstructionsLoaded?: (instructions: GetInstructionsApiResponseModel) => void;
  hideFooter?: boolean;
}

export default function ActionInstructions(props: props) {
  const notificationContext = useContext(NotificationContext);
  const [instructions, setInstructions] = useState<GetInstructionsApiResponseModel>(null);
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [canGetActions, setCanGetActions] = useState<boolean>(false);
  const [hasInputs, setHasInputs] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  useEffect(() => {
    setLoading(true);

    if (!props.action) {
      setInstructions(null);
      return;
    }

    getActionInstructions(
      Settings.profileId,
      props.integrationType,
      props.integrationSlug,
      props.action,
      props.accountId,
    ).then((r) => {
      if (!r.success && props.onError) {
        props.onError();
      }
      setCanGetActions(r.success);
      setInstructions(r.result);
      if (props.onInstructionsLoaded) {
        props.onInstructionsLoaded(r.result);
      }
      if (r.success && r.result.showInstructions) {
        setShowInstructions(true);
      }
      setHasInputs(SumNumber(r.result.steps.map((x) => x.inputs?.length || 0)) > 0);
      setLoading(false);
    });
  }, [props.integrationType, props.integrationSlug, props.action, props.accountId]);

  const onSubmit = (values: StrVal) => {
    if (instructions?.readonly === true) {
      return;
    }

    setSaving(true);
    setErrorMessages([]);
    postActionValues(
      Settings.profileId,
      props.integrationType,
      props.integrationSlug,
      props.action,
      values,
      props.accountId,
    ).then((r) => {
      if (r.success) {
        if (!!r.result.redirectUrl) {
          document.location.href = r.result.redirectUrl;
        } else {
          notificationContext.dispatch({
            type: 'ADD',
            payload: AccountActionResponseHelper(
              props.action,
              props.integrationType,
              props.integrationSlug,
              r.success,
              props.accountId,
              r.result.accountStatus === 'ConfigurationRequired',
            ),
          });
          props.onComplete(r.result.accountId, r.result.accountStatus === 'ConfigurationRequired' ? 'Configure' : null);
        }
      } else {
        setErrorMessages(ValidationErrorMessages(r.validationErrors, r.error?.code));
        setSaving(false);
      }
    });
  };

  if (!props?.action) {
    return null;
  }

  if (loading) {
    return (
      <P2GBox textAlign="center" p={4}>
        <P2GProgress />
      </P2GBox>
    );
  }

  if (!canGetActions) {
    return (
      <P2GBox>
        <P2GText>This action is unavailable.</P2GText>
      </P2GBox>
    );
  }

  return (
    <P2GFade in>
      <P2GBox key={`${props.integrationSlug}_${props.accountId}_${props.action}`}>
        {instructions.pleaseNoteText && (
          <P2GAlert color="info">
            <P2GText component="p">{instructions.pleaseNoteText}</P2GText>
          </P2GAlert>
        )}
        <P2GGrid container spacing={2} direction="column">
          {props.configRequired ? (
            <P2GGrid item>
              <P2GAlert color="info">
                <P2GText component="p">
                  Your account has been created, but there are some further settings you need to complete.
                </P2GText>
              </P2GAlert>
            </P2GGrid>
          ) : (
            instructions.headerText && (
              <P2GGrid item>
                <P2GText component="p">{instructions.headerText}</P2GText>
              </P2GGrid>
            )
          )}
          {hasInputs && (
            <P2GGrid item>
              {showInstructions ? (
                <P2GText component="p">
                  <P2GLink onClick={() => setShowInstructions(false)}>Turn instructions off.</P2GLink>
                </P2GText>
              ) : (
                <P2GText component="p">
                  Need help? <P2GLink onClick={() => setShowInstructions(true)}>View the instructions.</P2GLink>
                </P2GText>
              )}
            </P2GGrid>
          )}
          <P2GGrid item>
            <Steps
              hasInputs={hasInputs}
              action={props.action}
              steps={instructions.steps}
              terms={instructions.termsAndConditions}
              showInstructions={showInstructions}
              saving={saving}
              readonly={instructions.readonly}
              onSubmit={onSubmit}
            />
          </P2GGrid>
          {errorMessages.length > 0 && (
            <P2GGrid item>
              <P2GAlert color="error">
                {errorMessages.map((e) => (
                  <P2GText component="p" key={e}>
                    {e}
                  </P2GText>
                ))}
              </P2GAlert>
            </P2GGrid>
          )}
          {!props.hideFooter && instructions.footerText && (
            <>
              <P2GGrid item>
                <P2GDivider />
              </P2GGrid>
              <P2GGrid item>
                <InstructionsFooter children={instructions.footerText} />
              </P2GGrid>
            </>
          )}
        </P2GGrid>
      </P2GBox>
    </P2GFade>
  );
}
